import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import {
  articleHeroBannerVariants,
  articleHeroBannerColorConfig,
} from './article-hero-banner.variants';
import { Avatar, AvatarGroup } from '@/hen-ui/atomics/Avatar';
import { Link } from '@/hen-ui/atomics/Link';
import { Text } from '@/hen-ui/atomics/Text';
import { Button, type ButtonProps } from '@/hen-ui/atomics/Button';
import { Breadcrumb } from '@/hen-ui/composites/Breadcrumb';
import { Flex } from '@/hen-ui/composites/Flex';
import { SectionHeader } from '@/hen-ui/composites/SectionHeader';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import type { IconName } from '@/hen-ui/atomics/Icon';

export interface ArticleHeroContributor {
  name: string;
  initials: string;
  src?: string;
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
}

export interface ArticleHeroActionButtonConfig
  extends Omit<ButtonProps, 'children'> {
  text?: string;
}

/** Capturing split keeps surrounding copy; only the `<a>` chunk is remapped. */
const ADDITIONAL_BYLINE_PART =
  /(<a\b[^>]*\bhref\s*=\s*(?:"[^"]*"|'[^']*')[^>]*>[^<]*<\/a>)/gi;
const ADDITIONAL_BYLINE_HREF =
  /^<a\b[^>]*\bhref\s*=\s*(?:"([^"]*)"|'([^']*)')[^>]*>([^<]*)<\/a>$/i;

function ContributorCreditName({
  name,
  href,
  target,
  textColor,
  linkVariant,
}: {
  name: string;
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  textColor: 'subtext' | 'white';
  linkVariant: 'subtext' | 'white';
}) {
  if (!href) {
    return (
      <Text variant='b5' weight='bold' tag='span' color={textColor}>
        {name}
      </Text>
    );
  }
  return (
    <Text asChild variant='b5' weight='bold' color={textColor}>
      <Link
        href={href}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        variant={linkVariant}
        weight='bold'
        underline='hover'
        size='sm'
      >
        {name}
      </Link>
    </Text>
  );
}

/** Freeform CMS copy with optional inline `<a>`; sanitize upstream. */
function AdditionalByline({
  html,
  textColor,
  linkVariant,
}: {
  html: string;
  textColor: 'subtext' | 'white';
  linkVariant: 'subtext' | 'white';
}) {
  return (
    <Text variant='b5' tag='span' color={textColor}>
      {html.split(ADDITIONAL_BYLINE_PART).map((part, i) => {
        const anchor = part.match(ADDITIONAL_BYLINE_HREF);
        if (!anchor) return part;
        return (
          <ContributorCreditName
            key={i}
            name={anchor[3] ?? ''}
            href={anchor[1] ?? anchor[2]}
            textColor={textColor}
            linkVariant={linkVariant}
          />
        );
      })}
    </Text>
  );
}

/** Separator before the contributor at `index` in a list of `total` names. */
function nameSeparator(index: number, total: number): string {
  if (index === 0) return '';
  if (total === 2) return ' and ';
  if (index === total - 1) return ', and ';
  return ', ';
}

function ContributorCreditLine({
  label,
  people,
  textColor,
  linkVariant,
}: {
  label: string;
  people: ArticleHeroContributor[];
  textColor: 'subtext' | 'white';
  linkVariant: 'subtext' | 'white';
}) {
  if (people.length === 0) {
    return null;
  }
  return (
    <Flex asChild direction='row' gap='xxs'>
      <Text tag='span' variant='b5' color={textColor}>
        <Text
          variant='b5'
          tag='span'
          color={textColor}
          className='whitespace-nowrap'
        >
          {label}
        </Text>{' '}
        {/* All names live in one inline span so separators glue to names and
            the label-to-name spacing matches the single-author layout. */}
        <Text variant='b5' tag='span' color={textColor}>
          {people.map((person, i) => (
            <React.Fragment key={`${person.href ?? person.name}-${i}`}>
              {i > 0 ? nameSeparator(i, people.length) : null}
              <ContributorCreditName
                name={person.name}
                href={person.href}
                target={person.target}
                textColor={textColor}
                linkVariant={linkVariant}
              />
            </React.Fragment>
          ))}
        </Text>
      </Text>
    </Flex>
  );
}

export interface ArticleHeroBannerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Main title (H1) */
  title: string;
  /** Optional subtext or paragraph below the title */
  subtext?: string;
  /** Custom breadcrumb (full override). When not provided, uses Breadcrumb with breadcrumbItems. */
  breadcrumb?: React.ReactNode;
  /** Breadcrumb trail when using the default Breadcrumb. Omit or pass [] to hide the section; pass at least one item to show (e.g. [{ label: 'Home' }]). Ignored if breadcrumb is provided. */
  breadcrumbItems?: { label: string; href?: string }[];
  /** Optional action button top-right (e.g. "Save this article"). Uses Button props – text, href, iconBefore, iconAfter, etc. */
  showActionButton?: boolean;
  /** Action button text (e.g. "Save this article") */
  actionButtonText?: string;
  /** Action button icon (e.g. "heart") */
  actionButtonIcon?: IconName;
  /** Written by – one entry per author (name + optional avatar) */
  writtenBy?: ArticleHeroContributor[];
  /** Edited by – one entry per editor (name + optional avatar) */
  editedBy?: ArticleHeroContributor[];
  /** Reviewed by – one entry per reviewer (name + optional avatar) */
  reviewedBy?: ArticleHeroContributor[];
  /**
   * Optional sanitized HTML supplemental byline after Reviewed/Edited by
   * (e.g. “Fact-checked by…”). `<a>` tags are rendered with HEN {@link Link}
   * (same focus/hover styles as contributor credits). Used by BestColleges.
   */
  additionalByline?: string;
  /** Optional date string – e.g. published date (e.g. "September 25, 2025") */
  date?: string;
  /** Optional "Updated on" date – when set (e.g. from WordPress modified date), only "Updated on {date}" is shown (published date is not shown) */
  updatedOn?: string;
  /** Color scheme: neutral (white bg, dark text) or secondary (purple bg, white text) */
  color?: 'neutral' | 'secondary';
}

/**
 * ArticleHeroBanner component following HEN Design System standards.
 *
 * Hero section for article pages. Supports two color schemes: neutral (white bg,
 * dark text) or secondary (purple bg, white text). Optional breadcrumb, title,
 * subtext, contributor avatars and credits, and date.
 * Uses design tokens from tailwind.config.ts.
 *
 * @example
 * // Purple hero (default)
 * <ArticleHeroBanner
 *   color="secondary"
 *   title="Section Title"
 *   subtext="Subtext or Paragraph"
 *   breadcrumbItems={[{ label: "Home", href: "/" }, { label: "Section Title" }]}
 *   writtenBy={[{ name: "Dr. Tiffany Sanders", initials: "TS", href: "/contributors/dr-tiffany-sanders" }]}
 *   date="September 25, 2025"
 *   actionButtonIcon="heart"
 *   actionButtonText="Save this article to your dashboard"
 * />
 *
 * @example
 * // White / neutral
 * <ArticleHeroBanner color="neutral" title="Section Title" subtext="Subtext" />
 */
export const ArticleHeroBanner = React.forwardRef<
  HTMLDivElement,
  ArticleHeroBannerProps
>(
  (
    {
      className,
      title,
      subtext,
      breadcrumb,
      breadcrumbItems,
      showActionButton = true,
      actionButtonText = 'Save this article to your dashboard',
      actionButtonIcon = 'heart',
      writtenBy,
      editedBy,
      reviewedBy,
      additionalByline,
      date,
      updatedOn,
      color = 'neutral',
      ...props
    },
    ref,
  ) => {
    const content = articleHeroBannerColorConfig[color];
    const linkVariant = color === 'secondary' ? 'white' : 'subtext';
    const writers = writtenBy ?? [];
    const editors = editedBy ?? [];
    const reviewers = reviewedBy ?? [];
    const contributors = [...writers, ...editors, ...reviewers];
    const additionalBylineHtml = additionalByline?.trim() ?? '';
    const hasAdditionalByline = additionalBylineHtml.length > 0;
    const breadcrumbItemsResolved = breadcrumbItems ?? [];
    const hasBreadcrumbs =
      breadcrumb != null || breadcrumbItemsResolved.length > 0;

    return (
      <Flex
        ref={ref}
        direction='column'
        className={cn(articleHeroBannerVariants({ color }), className)}
        data-name='Article Hero Banner'
        {...props}
        data-hen-id={COMPONENT_IDS.ArticleHeroBanner}
      >
        <Flex
          direction='column'
          gap='lg'
          className='w-full max-w-content mx-auto shrink-0'
        >
          <Flex
            direction='row'
            wrap='wrap'
            align='center'
            justify={hasBreadcrumbs ? 'between' : 'end'}
            className='w-full shrink-0 gap-y-1'
          >
            {hasBreadcrumbs && (
              <Flex>
                {breadcrumb ?? (
                  <Breadcrumb
                    items={breadcrumbItemsResolved}
                    color={content.breadcrumbColor}
                    className='pl-0'
                  />
                )}
              </Flex>
            )}
            {showActionButton && (
              <Button
                variant={content.buttonVariant}
                iconAfter={actionButtonIcon}
                // todo: add onClick handler
                // onClick={actionButtonOnClick}
              >
                {actionButtonText}
              </Button>
            )}
          </Flex>

          {/* Title + subtext */}
          <SectionHeader
            size='h1'
            title={title}
            subtext={subtext}
            align='left'
            color={content.sectionHeaderColor}
          />

          {/* About article: avatars + credits + date */}
          <Flex direction='column' gap='xxs'>
            {(contributors.length > 0 || hasAdditionalByline) && (
              <Flex
                direction='column'
                gap='sm'
                className='w-full md:flex-row md:items-center'
              >
                {contributors.length > 0 && (
                  <AvatarGroup className={content.avatarGroupClassName}>
                    {contributors.map((c, i) => (
                      <Avatar
                        key={`${c.href ?? c.name}-${i}`}
                        alt={c.name}
                        fallback={c.initials}
                        src={c.src}
                        size='lg'
                      />
                    ))}
                  </AvatarGroup>
                )}
                <Flex
                  direction='column'
                  gap='xxs'
                  wrap='wrap'
                  className='min-w-0 flex-1 sm:flex-row sm:items-baseline sm:gap-sm'
                >
                  <ContributorCreditLine
                    label='Written by'
                    people={writers}
                    textColor={content.textColor}
                    linkVariant={linkVariant}
                  />
                  <ContributorCreditLine
                    label='Edited by'
                    people={editors}
                    textColor={content.textColor}
                    linkVariant={linkVariant}
                  />
                  <ContributorCreditLine
                    label='Reviewed by'
                    people={reviewers}
                    textColor={content.textColor}
                    linkVariant={linkVariant}
                  />
                  {hasAdditionalByline ? (
                    <AdditionalByline
                      html={additionalBylineHtml}
                      textColor={content.textColor}
                      linkVariant={linkVariant}
                    />
                  ) : null}
                </Flex>
              </Flex>
            )}
            {(date || updatedOn) && (
              <Flex direction='row' align='center' gap='xxs'>
                <Text variant='l1' color={content.textColor}>
                  {updatedOn ? `Updated on ${updatedOn}` : date}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    );
  },
);

ArticleHeroBanner.displayName = 'ArticleHeroBanner';
